/**
 * DataContext — single source of truth for all app data.
 * • Loads from Supabase on login, keeps localStorage as a sync cache.
 * • Exposes React state so pages never touch localStorage or storage.js.
 * • Provides cross-device sync via Supabase Realtime + page-visibility re-sync.
 */
import React, {
  createContext, useContext, useEffect, useCallback,
  useState, useRef, useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabaseClient';
import {
  fetchSettings, upsertSettings, upsertModel,
  fetchTemplates, upsertTemplates, deleteTemplateByCategory,
  fetchVocabulary, upsertVocabularyAll,
  fetchLogs, insertLog, deleteLogs,
  fetchFavorites, insertFavorite, deleteFavorite, updateFavorite as supabaseUpdateFavorite,
  fetchCategories, upsertCategories,
  fetchHistory, insertHistoryEntry, deleteHistory,
  fetchTranslationHistory, insertTranslationHistoryEntry, deleteTranslationHistory,
  logUsage as supabaseLogUsage,
  upsertVocabCollections,
} from '../lib/supabaseData';
import {
  DEFAULT_BRAND_GUIDELINES,
  DEFAULT_CATEGORY_TEMPLATES,
  DEFAULT_CATEGORIES,
} from '../lib/constants';

const DataContext = createContext(null);
const MIGRATION_KEY = 'vv_migration_v1_done';

// localStorage key map
const LS = {
  BG:         'vv_brand_guidelines',
  CT:         'vv_category_templates',
  HIST:       'vv_history',
  CATS:       'vv_categories',
  FAVS:       'vv_favorites',
  TRANS_HIST: 'vv_translation_history',
  VOCAB:      'vv_vocabulary',
  LOGS:       'vv_logs',
  MODEL:      'vv_model',
  VCOLS:      'vv_vocab_collections',
};

// ── localStorage read helpers ────────────────────────────────────────────────
function lsRead(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (parseErr) { console.error('[lsRead] Failed to parse localStorage key:', parseErr); return fallback; }
}
function lsStr(key, fallback = '') {
  return localStorage.getItem(key) || fallback;
}

// ── Supabase row normalisers ─────────────────────────────────────────────────
function normaliseLogs(rows) {
  return (rows || []).map(l => ({
    id: l.id,
    timestamp: l.created_at,
    type: l.type,
    fileName: l.file_name,
    productName: l.product_name,
    markets: l.markets || [],
    products: l.products,
    success: l.success,
    failed: l.failed,
    durationSeconds: l.duration_secs,
    perProduct: l.per_product,
    outputData: l.output_data || null,
  }));
}

function normaliseFavs(rows) {
  return (rows || []).map(f => ({
    id: f.id,
    productName: f.product_name,
    category: f.category,
    market: f.market,
    description: f.output,
    output: f.output,
    wordCount: f.word_count,
    features: f.features || '',
    metaTitle: f.meta_title || '',
    metaDescription: f.meta_description || '',
    createdAt: f.created_at,
  }));
}

function normaliseVocab(rows) {
  return (rows || []).map(v => ({
    id: v.id,
    termSE: v.term_se,
    category: v.category,
    translations: v.translations || {},
    status: v.status || 'Pending',
    notes: v.notes || '',
    collection: v.collection || 'Standard',
    createdAt: v.created_at,
  }));
}

// ── Stats helper (computed from history) ────────────────────────────────────
const CODE_TO_DISP = { SE: 'Sverige', NO: 'Norge', FI: 'Finland', DE: 'Tyskland', DK: 'Danmark', NL: 'Nederländerna' };
const normMkt = m => CODE_TO_DISP[m] || m;

function computeStats(history) {
  const now = Date.now();
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const weekAgo  = now - 7  * 24 * 3600 * 1000;
  const monthAgo = now - 30 * 24 * 3600 * 1000;

  const today     = history.filter(h => new Date(h.timestamp).getTime() >= todayStart);
  const thisWeek  = history.filter(h => new Date(h.timestamp).getTime() > weekAgo);
  const thisMonth = history.filter(h => new Date(h.timestamp).getTime() > monthAgo);

  const wordCounts = history.filter(h => h.wordCount > 0).map(h => h.wordCount);
  const avgWords   = wordCounts.length
    ? Math.round(wordCounts.reduce((a, b) => a + b, 0) / wordCounts.length) : 0;
  const inRange    = wordCounts.filter(wc => wc >= 150 && wc <= 220).length;
  const qualityRate = wordCounts.length
    ? Math.round((inRange / wordCounts.length) * 100) : 0;

  // Normalize market codes → display names to avoid duplicates
  const byMarket   = history.reduce((a, h) => {
    const m = normMkt(h.market);
    a[m] = (a[m] || 0) + 1; return a;
  }, {});
  const byCategory = history.reduce((a, h) => { a[h.category]  = (a[h.category]  || 0) + 1; return a; }, {});
  const bySource   = history.reduce((a, h) => {
    const s = h.source || 'single'; a[s] = (a[s] || 0) + 1; return a;
  }, {});

  const dailyBreakdown = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i); d.setHours(0, 0, 0, 0);
    const e = new Date(d); e.setHours(23, 59, 59, 999);
    const count = history.filter(h => {
      const ts = new Date(h.timestamp).getTime();
      return ts >= d.getTime() && ts <= e.getTime();
    }).length;
    dailyBreakdown.push({ date: d.toISOString().split('T')[0], dayName: d.toLocaleDateString('sv-SE', { weekday: 'short' }), count });
  }

  return {
    total: history.length, today: today.length,
    thisWeek: thisWeek.length, thisMonth: thisMonth.length,
    avgWords, qualityRate, byMarket, byCategory, bySource,
    dailyBreakdown, recent: history.slice(0, 20),
  };
}

// ── Provider ─────────────────────────────────────────────────────────────────
export function DataProvider({ children }) {
  const { user } = useAuth() || {};
  const uid = user?.id;
  const [migrationNeeded, setMigrationNeeded] = useState(false);
  const lastSyncRef = useRef(0);

  // State — initialised from localStorage cache (shows data instantly before Supabase sync)
  const [templates, setTemplates] = useState(() => ({
    brandGuidelines:  lsStr(LS.BG, DEFAULT_BRAND_GUIDELINES),
    categoryTemplates: lsRead(LS.CT, DEFAULT_CATEGORY_TEMPLATES),
  }));
  const [vocabulary,          setVocabulary]    = useState(() => lsRead(LS.VOCAB, []));
  const [logs,                setLogs]          = useState(() => lsRead(LS.LOGS, []));
  const [favorites,           setFavorites]     = useState(() => lsRead(LS.FAVS, []));
  const [categories,          setCategories]    = useState(() => {
    const s = lsRead(LS.CATS, null);
    return s?.length ? s : DEFAULT_CATEGORIES;
  });
  const [history,             setHistory]       = useState(() => lsRead(LS.HIST, []));
  const [translationHistory,  setTransHistory]  = useState(() => lsRead(LS.TRANS_HIST, []));
  const [model,               setModelState]    = useState(() => localStorage.getItem(LS.MODEL) || 'gemini');
  const [vocabCollections,    setVocabCollections] = useState(() => lsRead(LS.VCOLS, ['Standard']));

  const stats = useMemo(() => computeStats(history), [history]);

  // ── Supabase sync-down on login ────────────────────────────────────────────
  useEffect(() => {
    if (!uid) return;

    async function syncDown() {
      let supabaseHadData = false;
      try {
        const sett = await fetchSettings(uid);
        if (sett) {
          supabaseHadData = true;
          const bg = sett.brand_guidelines || '';
          localStorage.setItem(LS.BG, bg);
          setTemplates(prev => ({ ...prev, brandGuidelines: bg }));
          if (sett.model) {
            localStorage.setItem(LS.MODEL, sett.model);
            setModelState(sett.model);
          }
          if (sett.vocab_collections && sett.vocab_collections.length > 0) {
            localStorage.setItem(LS.VCOLS, JSON.stringify(sett.vocab_collections));
            setVocabCollections(sett.vocab_collections);
          }
        }

        const ct = await fetchTemplates(uid);
        if (ct && Object.keys(ct).length > 0) {
          supabaseHadData = true;
          localStorage.setItem(LS.CT, JSON.stringify(ct));
          setTemplates(prev => ({ ...prev, categoryTemplates: ct }));
        }

        const vRaw = await fetchVocabulary(uid);
        if (vRaw && vRaw.length > 0) {
          supabaseHadData = true;
          const norm = normaliseVocab(vRaw);
          localStorage.setItem(LS.VOCAB, JSON.stringify(norm));
          setVocabulary(norm);
        }

        const lRaw = await fetchLogs(uid);
        if (lRaw && lRaw.length > 0) {
          supabaseHadData = true;
          const norm = normaliseLogs(lRaw);
          localStorage.setItem(LS.LOGS, JSON.stringify(norm));
          setLogs(norm);
          window.dispatchEvent(new CustomEvent('vv-logs-updated'));
        }

        const fRaw = await fetchFavorites(uid);
        if (fRaw && fRaw.length > 0) {
          supabaseHadData = true;
          const norm = normaliseFavs(fRaw);
          localStorage.setItem(LS.FAVS, JSON.stringify(norm));
          setFavorites(norm);
          window.dispatchEvent(new CustomEvent('vv-favorites-updated'));
        }

        lastSyncRef.current = Date.now();
      } catch (err) {
        console.warn('[DataContext] syncDown error:', err);
      }

      // ── Categories (separate fetch — column may not exist pre-migration) ────
      try {
        const catRow = await fetchCategories(uid);
        // Läs senaste templates-map (från syncDown ovan) för att kunna läka categories-listan
        // — om ett template finns för en kategori som saknas i app_settings.categories vill vi
        // se den ändå. Sker automatiskt post-V20 när flera användare delar workspace.
        const templatesMap = lsRead(LS.CT, {});
        const templateCats = Object.keys(templatesMap).filter(c => c && String(c).trim().length > 0);

        let merged;
        if (catRow && catRow.length > 0) {
          const union = [...catRow];
          templateCats.forEach(c => { if (!union.includes(c)) union.push(c); });
          merged = union;
        } else {
          // Ingen rad i app_settings ännu — auto-migrera med union av local + template-kategorier
          const localCats = lsRead(LS.CATS, null);
          const base = (localCats && localCats.length > 0) ? localCats : DEFAULT_CATEGORIES;
          merged = [...base];
          templateCats.forEach(c => { if (!merged.includes(c)) merged.push(c); });
        }

        localStorage.setItem(LS.CATS, JSON.stringify(merged));
        setCategories(merged);

        // Skriv tillbaka läkta listan till app_settings om den skiljer sig från serverns
        const changed = !catRow || catRow.length !== merged.length ||
                        merged.some((c, i) => c !== catRow[i]);
        if (changed) upsertCategories(uid, merged).catch(() => {});
      } catch (e) { /* pre-migration: categories column may not exist */ }

      // ── History (table may not exist pre-migration) ─────────────────────────
      try {
        const histRaw = await fetchHistory(uid);
        if (histRaw && histRaw.length > 0) {
          const norm = histRaw.map(h => ({
            id: h.id, timestamp: h.created_at, productName: h.product_name,
            category: h.category, market: h.market, wordCount: h.word_count,
            source: h.source || 'single',
          }));
          localStorage.setItem(LS.HIST, JSON.stringify(norm));
          setHistory(norm);
        } else if (histRaw !== null) {
          // Table exists but is empty — auto-migrate localStorage history
          const localHist = lsRead(LS.HIST, []);
          if (localHist.length > 0)
            localHist.slice(0, 500).forEach(h => insertHistoryEntry(uid, h).catch(() => {}));
        }
      } catch (e) { /* pre-migration: history table may not exist */ }

      // ── Translation history (table may not exist pre-migration) ────────────
      try {
        const transRaw = await fetchTranslationHistory(uid);
        if (transRaw && transRaw.length > 0) {
          const norm = transRaw.map(t => ({
            id: t.id, timestamp: t.created_at, snippet: t.snippet,
            markets: t.markets || [], sourceLang: t.source_lang,
          }));
          localStorage.setItem(LS.TRANS_HIST, JSON.stringify(norm));
          setTransHistory(norm);
        } else if (transRaw !== null) {
          // Table exists but is empty — auto-migrate localStorage translation history
          const localTrans = lsRead(LS.TRANS_HIST, []);
          if (localTrans.length > 0)
            localTrans.slice(0, 200).forEach(t => insertTranslationHistoryEntry(uid, t).catch(() => {}));
        }
      } catch (e) { /* pre-migration: translation_history table may not exist */ }

      // Migration wizard detection
      const migDone = localStorage.getItem(MIGRATION_KEY);
      if (!migDone && !supabaseHadData) {
        const lv = lsRead(LS.VOCAB, []);
        const lf = lsRead(LS.FAVS, []);
        const lb = lsStr(LS.BG);
        const ll = lsRead(LS.LOGS, []);
        const hasMeaningfulData = lv.length > 0 || lf.length > 0 || ll.length > 0 || lb.length > 100;
        if (hasMeaningfulData) {
          setMigrationNeeded(true);
        } else {
          localStorage.setItem(MIGRATION_KEY, '1');
        }
      }
    }

    syncDown();
  }, [uid]);

  // ── Cross-device re-sync (visibility) ─────────────────────────────────────
  const doRefreshLogs = useCallback(async () => {
    if (!uid) return;
    const rows = await fetchLogs(uid);
    const norm = normaliseLogs(rows);
    localStorage.setItem(LS.LOGS, JSON.stringify(norm));
    setLogs(norm);
    window.dispatchEvent(new CustomEvent('vv-logs-updated'));
  }, [uid]);

  const doRefreshFavorites = useCallback(async () => {
    if (!uid) return;
    const rows = await fetchFavorites(uid);
    const norm = normaliseFavs(rows);
    localStorage.setItem(LS.FAVS, JSON.stringify(norm));
    setFavorites(norm);
    window.dispatchEvent(new CustomEvent('vv-favorites-updated'));
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    const MIN = 30_000;
    const handle = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastSyncRef.current;
        if (elapsed > MIN) {
          lastSyncRef.current = Date.now();
          doRefreshLogs().catch(() => {});
          doRefreshFavorites().catch(() => {});
        }
      }
    };
    document.addEventListener('visibilitychange', handle);
    return () => document.removeEventListener('visibilitychange', handle);
  }, [uid, doRefreshLogs, doRefreshFavorites]);

  // ── Supabase Realtime ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!uid || !supabase) return;
    const channel = supabase
      .channel(`vv-realtime-${uid}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'logs', filter: `user_id=eq.${uid}` },
        () => doRefreshLogs().catch(() => {})
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'favorites', filter: `user_id=eq.${uid}` },
        () => doRefreshFavorites().catch(() => {})
      )
      .subscribe(s => console.log('[DataContext] Realtime:', s));
    return () => { supabase.removeChannel(channel); };
  }, [uid, doRefreshLogs, doRefreshFavorites]);

  // ── Migration ──────────────────────────────────────────────────────────────
  const runMigration = useCallback(async () => {
    if (!uid) return { ok: false };
    try {
      const bg     = lsStr(LS.BG);
      const ct     = lsRead(LS.CT, {});
      const vocab  = lsRead(LS.VOCAB, []);
      const favs   = lsRead(LS.FAVS, []);
      const logsData = lsRead(LS.LOGS, []);
      const cats   = lsRead(LS.CATS, null);
      const histData = lsRead(LS.HIST, []);
      const transData = lsRead(LS.TRANS_HIST, []);

      await upsertSettings(uid, bg);
      await upsertTemplates(uid, ct);
      if (cats && cats.length > 0) await upsertCategories(uid, cats);
      if (vocab.length > 0) await upsertVocabularyAll(uid, vocab);
      for (const l of logsData.slice(0, 200)) await insertLog(uid, l);
      const recentFavs = favs.slice(0, 100);
      for (const f of recentFavs) await insertFavorite(uid, f);
      for (const h of histData.slice(0, 500)) await insertHistoryEntry(uid, h);
      for (const t of transData.slice(0, 200)) await insertTranslationHistoryEntry(uid, t);

      localStorage.setItem(MIGRATION_KEY, '1');
      [LS.BG, LS.CT, LS.VOCAB, LS.FAVS, LS.LOGS, LS.CATS, LS.HIST, LS.TRANS_HIST]
        .forEach(k => localStorage.removeItem(k));

      // Re-sync state from Supabase
      const [sett, ctF, vRaw, lRaw, fRaw, histRaw, transRaw] = await Promise.all([
        fetchSettings(uid), fetchTemplates(uid), fetchVocabulary(uid),
        fetchLogs(uid), fetchFavorites(uid), fetchHistory(uid), fetchTranslationHistory(uid),
      ]);
      if (sett) {
        localStorage.setItem(LS.BG, sett.brand_guidelines || '');
        setTemplates(prev => ({ ...prev, brandGuidelines: sett.brand_guidelines || '' }));
        if (sett.categories && sett.categories.length > 0) {
          localStorage.setItem(LS.CATS, JSON.stringify(sett.categories));
          setCategories(sett.categories);
        }
      }
      if (ctF && Object.keys(ctF).length > 0) { localStorage.setItem(LS.CT, JSON.stringify(ctF)); setTemplates(prev => ({ ...prev, categoryTemplates: ctF })); }
      if (vRaw?.length) { const n = normaliseVocab(vRaw); localStorage.setItem(LS.VOCAB, JSON.stringify(n)); setVocabulary(n); }
      if (lRaw?.length) { const n = normaliseLogs(lRaw); localStorage.setItem(LS.LOGS, JSON.stringify(n)); setLogs(n); }
      if (fRaw?.length) { const n = normaliseFavs(fRaw); localStorage.setItem(LS.FAVS, JSON.stringify(n)); setFavorites(n); }
      if (histRaw?.length) {
        const n = histRaw.map(h => ({ id: h.id, timestamp: h.created_at, productName: h.product_name, category: h.category, market: h.market, wordCount: h.word_count, source: h.source || 'single' }));
        localStorage.setItem(LS.HIST, JSON.stringify(n)); setHistory(n);
      }
      if (transRaw?.length) {
        const n = transRaw.map(t => ({
          id: t.id, timestamp: t.created_at, snippet: t.snippet,
          markets: t.markets || [], sourceLang: t.source_lang,
          fullText: t.full_text || null, results: t.results_json || null,
        }));
        localStorage.setItem(LS.TRANS_HIST, JSON.stringify(n)); setTransHistory(n);
      }

      return { ok: true, vocab: vocab.length, favs: recentFavs.length, logs: logsData.slice(0, 200).length };
    } catch (err) {
      console.error('[DataContext] migration error:', err);
      return { ok: false, error: err.message };
    }
  }, [uid]);

  const skipMigration = useCallback(() => {
    localStorage.setItem(MIGRATION_KEY, '1');
    setMigrationNeeded(false);
  }, []);

  // ── Templates ─────────────────────────────────────────────────────────────
  const saveTemplates = useCallback(async (brandGuidelines, categoryTemplates) => {
    localStorage.setItem(LS.BG, brandGuidelines);
    localStorage.setItem(LS.CT, JSON.stringify(categoryTemplates));
    setTemplates({ brandGuidelines, categoryTemplates });
    if (uid) {
      // Await + re-throw så TemplatesPage kan visa toast om Supabase-anropet misslyckas.
      // Brand guidelines skrivs mot app_settings (V20) — misslyckas oftast pga RLS/nätverk.
      await Promise.all([
        upsertSettings(uid, brandGuidelines),
        upsertTemplates(uid, categoryTemplates),
      ]);
    }
  }, [uid]);

  const resetTemplates = useCallback(() => {
    [LS.BG, LS.CT, LS.CATS].forEach(k => localStorage.removeItem(k));
    setTemplates({ brandGuidelines: DEFAULT_BRAND_GUIDELINES, categoryTemplates: DEFAULT_CATEGORY_TEMPLATES });
    setCategories(DEFAULT_CATEGORIES);
    if (uid) {
      upsertSettings(uid, DEFAULT_BRAND_GUIDELINES).catch(() => {});
      upsertTemplates(uid, DEFAULT_CATEGORY_TEMPLATES).catch(() => {});
    }
  }, [uid]);

  // ── Categories ────────────────────────────────────────────────────────────
  const saveCategories = useCallback((cats) => {
    localStorage.setItem(LS.CATS, JSON.stringify(cats));
    setCategories(cats);
    if (uid) upsertCategories(uid, cats).catch(() => {});
  }, [uid]);

  const addCategory = useCallback((name) => {
    if (categories.includes(name)) return;
    const next = [...categories, name];
    localStorage.setItem(LS.CATS, JSON.stringify(next));
    setCategories(next);
    if (uid) upsertCategories(uid, next).catch(() => {});
  }, [uid, categories]);

  const removeCategory = useCallback((name) => {
    const next = categories.filter(c => c !== name);
    localStorage.setItem(LS.CATS, JSON.stringify(next));
    setCategories(next);
    if (uid) {
      upsertCategories(uid, next).catch(() => {});
      // Explicit rad-radering — annars ligger malls-raden kvar i DB efter kategorin försvinner.
      deleteTemplateByCategory(name).catch(() => {});
    }
    setTemplates(prev => {
      const ct = { ...prev.categoryTemplates };
      delete ct[name];
      localStorage.setItem(LS.CT, JSON.stringify(ct));
      return { ...prev, categoryTemplates: ct };
    });
  }, [uid, categories]);

  // ── Vocabulary ────────────────────────────────────────────────────────────
  const saveVocabulary = useCallback((entries) => {
    localStorage.setItem(LS.VOCAB, JSON.stringify(entries));
    setVocabulary(entries);
    if (uid) upsertVocabularyAll(uid, entries).catch(() => {});
  }, [uid]);

  const addVocabEntry = useCallback((entry) => {
    const newEntry = { id: entry.id || Date.now(), createdAt: entry.createdAt || new Date().toISOString(), ...entry };
    const next = [newEntry, ...vocabulary];  // closure over vocabulary — safe for single add
    localStorage.setItem(LS.VOCAB, JSON.stringify(next));
    setVocabulary(next);
    if (uid) upsertVocabularyAll(uid, next).catch(() => {});
    return newEntry;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, vocabulary]);

  const updateVocabEntry = useCallback((id, updates) => {
    const next = vocabulary.map(e => e.id === id ? { ...e, ...updates } : e);
    localStorage.setItem(LS.VOCAB, JSON.stringify(next));
    setVocabulary(next);
    if (uid) upsertVocabularyAll(uid, next).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, vocabulary]);

  const deleteVocabEntry = useCallback((id) => {
    const next = vocabulary.filter(e => e.id !== id);
    localStorage.setItem(LS.VOCAB, JSON.stringify(next));
    setVocabulary(next);
    if (uid) upsertVocabularyAll(uid, next).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, vocabulary]);

  // ── Logs ──────────────────────────────────────────────────────────────────
  const addLog = useCallback((entry) => {
    const log = { id: entry.id || Date.now(), timestamp: entry.timestamp || new Date().toISOString(), ...entry };
    setLogs(prev => {
      const next = [log, ...prev].slice(0, 500);
      localStorage.setItem(LS.LOGS, JSON.stringify(next));
      return next;
    });
    if (uid) insertLog(uid, log).catch(() => {});
  }, [uid]);

  const clearLogs = useCallback(() => {
    localStorage.removeItem(LS.LOGS);
    setLogs([]);
    if (uid) deleteLogs(uid).catch(() => {});
    window.dispatchEvent(new CustomEvent('vv-logs-updated'));
  }, [uid]);

  // ── Favorites ─────────────────────────────────────────────────────────────
  const addFavorite = useCallback((entry) => {
    setFavorites(prev => {
      if (prev.some(f => f.id === entry.id)) return prev;
      const newEntry = { id: entry.id || Date.now(), savedAt: new Date().toISOString(), ...entry };
      const next = [newEntry, ...prev].slice(0, 200);
      localStorage.setItem(LS.FAVS, JSON.stringify(next));
      return next;
    });
    if (uid) insertFavorite(uid, entry).catch(() => {});
  }, [uid]);

  const removeFavorite = useCallback((id) => {
    setFavorites(prev => {
      const next = prev.filter(f => f.id !== id);
      localStorage.setItem(LS.FAVS, JSON.stringify(next));
      return next;
    });
    if (uid) deleteFavorite(uid, id).catch(() => {});
  }, [uid]);

  const updateFavorite = useCallback((id, updates) => {
    setFavorites(prev => {
      const next = prev.map(f => f.id === id ? { ...f, ...updates } : f);
      localStorage.setItem(LS.FAVS, JSON.stringify(next));
      return next;
    });
    if (uid) supabaseUpdateFavorite(uid, id, updates).catch(() => {});
  }, [uid]);

  // ── History (Supabase-backed) ─────────────────────────────────────────────
  const addToHistory = useCallback((entry) => {
    const newEntry = { id: Date.now(), timestamp: new Date().toISOString(), ...entry };
    setHistory(prev => {
      const next = [newEntry, ...prev].slice(0, 1000);
      localStorage.setItem(LS.HIST, JSON.stringify(next));
      return next;
    });
    if (uid) insertHistoryEntry(uid, newEntry).catch(() => {});
  }, [uid]);

  const clearHistory = useCallback(() => {
    localStorage.removeItem(LS.HIST);
    setHistory([]);
    if (uid) deleteHistory(uid).catch(() => {});
  }, [uid]);

  // ── Model preference ─────────────────────────────────────────────────────
  const saveModel = useCallback((m) => {
    localStorage.setItem(LS.MODEL, m);
    setModelState(m);
    if (uid) upsertModel(uid, m).catch(() => {});
  }, [uid]);

  // ── Vocab collections ─────────────────────────────────────────────────────
  const saveVocabCollections = useCallback((collections) => {
    const next = collections && collections.length > 0 ? collections : ['Standard'];
    localStorage.setItem(LS.VCOLS, JSON.stringify(next));
    setVocabCollections(next);
    if (uid) upsertVocabCollections(uid, next).catch(() => {});
  }, [uid]);

  // ── Usage logging ──────────────────────────────────────────────────────────
  const addUsageLog = useCallback((entry) => {
    if (uid) supabaseLogUsage(uid, { userEmail: user?.email, ...entry }).catch(() => {});
  }, [uid, user?.email]);

  // ── Translation history (Supabase-backed) ─────────────────────────────────
  const addToTranslationHistory = useCallback(({ snippet, markets, sourceLang, fullText, results }) => {
    const entry = {
      id: Date.now(), timestamp: new Date().toISOString(),
      snippet: (snippet || '').slice(0, 60),
      markets, sourceLang,
      fullText: fullText || null,
      results: results || null,
    };
    setTransHistory(prev => {
      const next = [entry, ...prev].slice(0, 200);
      localStorage.setItem(LS.TRANS_HIST, JSON.stringify(next));
      return next;
    });
    if (uid) insertTranslationHistoryEntry(uid, entry).catch(() => {});
    return entry;
  }, [uid]);

  const clearTranslationHistory = useCallback(() => {
    localStorage.removeItem(LS.TRANS_HIST);
    setTransHistory([]);
    if (uid) deleteTranslationHistory(uid).catch(() => {});
  }, [uid]);

  return (
    <DataContext.Provider value={{
      // ── Read state ──────────────────────────────────────────────────────
      templates,
      vocabulary,
      logs,
      favorites,
      categories,
      history,
      translationHistory,
      stats,
      // ── Template ops ────────────────────────────────────────────────────
      saveTemplates,
      resetTemplates,
      // ── Category ops ────────────────────────────────────────────────────
      saveCategories,
      addCategory,
      removeCategory,
      // ── Vocabulary ops ──────────────────────────────────────────────────
      saveVocabulary,
      addVocabEntry,
      updateVocabEntry,
      deleteVocabEntry,
      // ── Log ops ─────────────────────────────────────────────────────────
      addLog,
      clearLogs,
      // ── Favorite ops ────────────────────────────────────────────────────
      addFavorite,
      removeFavorite,
      updateFavorite,
      // ── History ops (Supabase-backed) ────────────────────────────────────
      addToHistory,
      clearHistory,
      // ── Translation history ops (Supabase-backed) ────────────────────────
      addToTranslationHistory,
      clearTranslationHistory,
      // ── Model ───────────────────────────────────────────────────────────
      model,
      saveModel,
      // ── Vocab Collections ────────────────────────────────────────────────
      vocabCollections,
      saveVocabCollections,
      // ── Usage ───────────────────────────────────────────────────────────
      addUsageLog,
      // ── Migration ───────────────────────────────────────────────────────
      migrationNeeded,
      runMigration,
      skipMigration,
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  return useContext(DataContext);
}
